import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import {
    Search, Building2, Phone, Mail, MapPin,
    Clock, CheckCircle, Filter, Eye,
    MessageSquare, Star, Briefcase,
    Globe, Heart
} from 'lucide-react';
import PageHeader from './common/PageHeader';

function LabDirectory({ user }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('lab');
    const [labs, setLabs] = useState([]);
    const [filteredLabs, setFilteredLabs] = useState([]);
    const [outsourceCompanies, setOutsourceCompanies] = useState([]);
    const [filteredOutsource, setFilteredOutsource] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterSpecialty, setFilterSpecialty] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [favoriteLabIds, setFavoriteLabIds] = useState(new Set());
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

    const specialtyOptions = [
        'all', '크라운/브릿지', '임플란트', '임플란트 보철', '교정장치', '틀니',
        '라미네이트', '지르코니아', '금속도재', '올세라믹', '투명교정', '스플린트',
        'CAD/CAM 디지털 기공', '라미네이트/비니어', '임시 보철'
    ];

    const outsourceCategoryOptions = [
        { id: 'all', label: '전체 카테고리' },
        { id: 'custom_milling', label: 'Custom Milling' },
        { id: '3d_printing', label: '3D Printing' },
        { id: 'design', label: 'Design' },
        { id: 'etc', label: '기타' }
    ];
    const catLabel = (id) => outsourceCategoryOptions.find(c => c.id === id)?.label || id;

    // helpers
    const getServiceNames = (lab) => {
        if (Array.isArray(lab.services)) return lab.services.map(s => typeof s === 'string' ? s : s.name);
        if (lab.specialties) return lab.specialties;
        return [];
    };
    const getIntro = (lab) => lab.philosophy || lab.introduction || '';

    // 관심 기공소 로드
    const loadFavorites = useCallback(async () => {
        try {
            const uid = user?.companyId || user?.uid;
            if (!uid) return;
            const favDoc = await getDoc(doc(db, 'userFavorites', uid));
            if (favDoc.exists()) {
                setFavoriteLabIds(new Set(favDoc.data().labIds || []));
            }
        } catch (e) {
            console.error('관심 기공소 로드 실패:', e);
        }
    }, [user]);

    // 관심 기공소 토글
    const toggleFavorite = async (e, labId) => {
        e.stopPropagation();
        const uid = user?.companyId || user?.uid;
        if (!uid) return;

        const newFavs = new Set(favoriteLabIds);
        if (newFavs.has(labId)) {
            newFavs.delete(labId);
        } else {
            newFavs.add(labId);
        }
        setFavoriteLabIds(newFavs);

        try {
            await setDoc(doc(db, 'userFavorites', uid), {
                labIds: Array.from(newFavs),
                updatedAt: new Date(),
            });
        } catch (e) {
            console.error('관심 기공소 저장 실패:', e);
        }
    };

    useEffect(() => { loadFavorites(); }, [loadFavorites]);

    const loadLabs = async () => {
        try {
            setLoading(true);
            const adsRef = collection(db, 'labAdvertisements');
            const q = query(adsRef, where('isActive', '==', true));
            const snapshot = await getDocs(q);
            const labsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLabs(labsData);
            setFilteredLabs(labsData);
        } catch (error) {
            console.error('기공소 목록 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadOutsourceCompanies = async () => {
        try {
            const adsRef = collection(db, 'outsourceAdvertisements');
            const q = query(adsRef, where('isActive', '==', true));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setOutsourceCompanies(data);
            setFilteredOutsource(data);
        } catch (error) {
            console.error('외주업체 목록 로드 실패:', error);
        }
    };

    const filterLabs = useCallback(() => {
        let filtered = labs;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(lab =>
                (lab.businessName || '').toLowerCase().includes(q) ||
                (lab.philosophy || lab.introduction || '').toLowerCase().includes(q) ||
                (lab.address || '').toLowerCase().includes(q) ||
                (lab.slogan || '').toLowerCase().includes(q)
            );
        }
        if (filterSpecialty !== 'all') {
            filtered = filtered.filter(lab => {
                const names = getServiceNames(lab);
                return names.some(n => n.includes(filterSpecialty) || filterSpecialty.includes(n));
            });
        }
        setFilteredLabs(filtered);
    }, [labs, searchQuery, filterSpecialty]);

    const filterOutsource = useCallback(() => {
        let filtered = outsourceCompanies;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                (item.businessName || '').toLowerCase().includes(q) ||
                (item.introduction || '').toLowerCase().includes(q)
            );
        }
        if (filterCategory !== 'all') {
            filtered = filtered.filter(item => item.categories && item.categories.includes(filterCategory));
        }
        setFilteredOutsource(filtered);
    }, [outsourceCompanies, searchQuery, filterCategory]);

    useEffect(() => { loadLabs(); loadOutsourceCompanies(); }, []);
    useEffect(() => { filterLabs(); }, [filterLabs]);
    useEffect(() => { filterOutsource(); }, [filterOutsource]);

    const handleCardClick = (lab) => { navigate(`/lab/${lab.labId || lab.id}`); };
    const handleStartChat = (lab, e) => {
        e.stopPropagation();
        const labId = lab.labId || lab.outsourceId || lab.userId || lab.id;
        navigate(`/chat?partnerId=${labId}`);
    };

    const currentList = activeTab === 'lab'
        ? (showFavoritesOnly ? filteredLabs.filter(l => favoriteLabIds.has(l.labId || l.id)) : filteredLabs)
        : filteredOutsource;

    if (loading) {
        return (
            <div style={S.loading}>
                <div style={S.spinner} />
                <p>로딩 중...</p>
            </div>
        );
    }

    // =========================================================================
    // 잡코리아 스타일 카드 렌더
    // =========================================================================
    const renderLabCard = (lab) => {
        const services = getServiceNames(lab);
        const prices = Array.isArray(lab.priceTable) ? lab.priceTable : [];
        const scanners = Array.isArray(lab.compatibleScanners) ? lab.compatibleScanners : [];
        const fmtPrice = (p) => {
            const n = parseInt(p);
            if (isNaN(n)) return p;
            return n >= 10000 ? `${Math.floor(n/10000)}만${n%10000>=1000?Math.floor((n%10000)/1000)+'천':''}` : `${n.toLocaleString()}`;
        };
        // 주요 단가 2~3개
        const keyPrices = prices.filter(p => p.price).slice(0, 3);

        return (
            <div key={lab.id} style={S.card} onClick={() => handleCardClick(lab)}>
                {/* 프로필 헤더 */}
                <div style={S.cardHeader}>
                    <div style={S.cardAvatar}>
                        {lab.profileImageUrl ? (
                            <img src={lab.profileImageUrl} alt="" style={S.cardAvatarImg} />
                        ) : (
                            <Building2 size={22} color="#94a3b8" />
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={S.cardTitle}>{lab.businessName || '기공소'}</h3>
                        {lab.slogan && <p style={S.cardSlogan}>{lab.slogan}</p>}
                    </div>
                    <button
                        onClick={(e) => toggleFavorite(e, lab.labId || lab.id)}
                        style={S.heartBtn}
                        title={favoriteLabIds.has(lab.labId || lab.id) ? '관심 해제' : '관심 등록'}
                    >
                        <Heart
                            size={20}
                            color={favoriteLabIds.has(lab.labId || lab.id) ? '#ef4444' : '#cbd5e1'}
                            fill={favoriteLabIds.has(lab.labId || lab.id) ? '#ef4444' : 'none'}
                        />
                    </button>
                </div>

                <div style={S.cardBody}>
                    {/* 단가 핵심 */}
                    {keyPrices.length > 0 && (
                        <div style={S.cardPriceRow}>
                            {keyPrices.map((p, i) => (
                                <span key={i} style={S.cardPrice}>
                                    {(p.item || '').replace(' 크라운','').replace('커스텀 ','')} <strong>{fmtPrice(p.price)}</strong>
                                    {p.deliveryDays && <span style={S.cardDays}>/{p.deliveryDays}일</span>}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* 스펙 배지 */}
                    <div style={S.cardSpecs}>
                        {scanners.length > 0 && <span style={S.specBadge}>스캐너 {scanners.length}종</span>}
                        {lab.remakeRate && <span style={S.specBadge}>리메이크 {lab.remakeRate}%</span>}
                        {lab.monthlyCapacity && <span style={S.specBadge}>월 {lab.monthlyCapacity}건</span>}
                    </div>

                    {/* 첫 거래 혜택 */}
                    {lab.firstDealBenefit && (
                        <div style={S.cardBenefit}>🏷️ {lab.firstDealBenefit}</div>
                    )}

                    {/* 전문분야 */}
                    {services.length > 0 && (
                        <div style={S.cardTags}>
                            {services.slice(0, 3).map((s, i) => (
                                <span key={i} style={S.cardTag}>{s}</span>
                            ))}
                            {services.length > 3 && <span style={S.cardTagMore}>+{services.length - 3}</span>}
                        </div>
                    )}

                    {/* 위치 */}
                    {lab.address && (
                        <div style={S.cardInfoRow}>
                            <MapPin size={12} color="#94a3b8" />
                            <span>{lab.address}</span>
                        </div>
                    )}

                    {/* 버튼 */}
                    <div style={S.cardActions}>
                        <button style={S.btnPrimary}><Eye size={14} />상세보기</button>
                    </div>
                </div>
            </div>
        );
    };

    // =========================================================================
    // 외주업체 카드 (기존 유지)
    // =========================================================================
    const renderOutsourceCard = (lab) => (
        <div key={lab.id} style={S.card} onClick={() => handleCardClick(lab)}>
            <div style={S.cardBanner}>
                {lab.profileImageUrl ? (
                    <img src={lab.profileImageUrl} alt="" style={S.cardBannerImg} />
                ) : (
                    <div style={S.cardBannerPlaceholder}><Building2 size={32} color="#cbd5e1" /></div>
                )}
            </div>
            <div style={S.cardBody}>
                <h3 style={S.cardTitle}>{lab.businessName || '외주업체'}</h3>
                {lab.categories && lab.categories.length > 0 && (
                    <div style={S.cardTags}>
                        {lab.categories.map(cat => (
                            <span key={cat} style={{ ...S.cardTag, backgroundColor: '#ede9fe', color: '#7c3aed' }}>{catLabel(cat)}</span>
                        ))}
                    </div>
                )}
                {lab.introduction && <p style={S.cardIntro}>{lab.introduction.length > 80 ? lab.introduction.substring(0, 80) + '...' : lab.introduction}</p>}
                {lab.address && <div style={S.cardInfoRow}><MapPin size={13} color="#94a3b8" /><span>{lab.address}</span></div>}
                <div style={S.cardActions}>
                    <button style={S.btnPrimary}><Eye size={15} />상세보기</button>
                </div>
            </div>
        </div>
    );

    return (
        <div style={S.container}>
            <PageHeader icon={Building2} title="업체소개" />

            {/* 탭 */}
            <div style={S.tabRow}>
                <button style={{ ...S.tab, ...(activeTab === 'lab' ? S.tabActive : {}) }}
                    onClick={() => { setActiveTab('lab'); setSearchQuery(''); setFilterSpecialty('all'); }}>
                    기공소 <span style={S.tabCount}>{labs.length}</span>
                </button>
                <button style={{ ...S.tab, ...(activeTab === 'outsource' ? S.tabActivePurple : {}) }}
                    onClick={() => { setActiveTab('outsource'); setSearchQuery(''); setFilterCategory('all'); }}>
                    외주업체 <span style={S.tabCount}>{outsourceCompanies.length}</span>
                </button>
                {activeTab === 'lab' && (
                    <button
                        style={{ ...S.tab, ...(showFavoritesOnly ? { borderColor: '#ef4444', backgroundColor: '#fef2f2', color: '#ef4444' } : {}), marginLeft: 'auto' }}
                        onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    >
                        <Heart size={16} fill={showFavoritesOnly ? '#ef4444' : 'none'} color={showFavoritesOnly ? '#ef4444' : '#64748b'} />
                        관심 기공소 <span style={S.tabCount}>{favoriteLabIds.size}</span>
                    </button>
                )}
            </div>

            {/* 검색 */}
            <div style={S.searchRow}>
                <div style={S.searchBox}>
                    <Search size={20} color="#64748b" />
                    <input type="text" placeholder={activeTab === 'lab' ? '기공소명, 지역, 서비스 등으로 검색...' : '외주업체명, 지역 등으로 검색...'}
                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={S.searchInput} />
                </div>
                {activeTab === 'lab' ? (
                    <div style={S.filterBox}>
                        <Filter size={18} color="#64748b" />
                        <select value={filterSpecialty} onChange={e => setFilterSpecialty(e.target.value)} style={S.filterSelect}>
                            {specialtyOptions.map(o => <option key={o} value={o}>{o === 'all' ? '전체 분야' : o}</option>)}
                        </select>
                    </div>
                ) : (
                    <div style={S.filterBox}>
                        <Filter size={18} color="#64748b" />
                        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={S.filterSelect}>
                            {outsourceCategoryOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </select>
                    </div>
                )}
            </div>

            {/* 결과 */}
            {currentList.length === 0 ? (
                <div style={S.empty}>
                    <Building2 size={56} color="#cbd5e1" />
                    <p style={S.emptyTitle}>등록된 업체가 없습니다</p>
                    <p style={S.emptySub}>다른 검색어나 필터를 시도해보세요</p>
                </div>
            ) : (
                <div style={S.grid}>
                    {currentList.map(lab => activeTab === 'lab' ? renderLabCard(lab) : renderOutsourceCard(lab))}
                </div>
            )}

        </div>
    );
}

// =========================================================================
// 스타일 (잡코리아 스타일)
// =========================================================================
const S = {
    container: { maxWidth: '1400px', margin: '0 auto', padding: '20px' },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#64748b' },
    spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },

    // 탭
    tabRow: { display: 'flex', gap: '8px', marginBottom: '24px' },
    tab: { padding: '12px 24px', border: '2px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' },
    tabActive: { borderColor: '#6366f1', backgroundColor: '#eef2ff', color: '#6366f1' },
    tabActivePurple: { borderColor: '#8b5cf6', backgroundColor: '#f5f3ff', color: '#8b5cf6' },
    tabCount: { fontSize: '12px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#f1f5f9', color: '#64748b' },

    // 검색
    searchRow: { display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '280px', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', backgroundColor: '#fff', border: '2px solid #e2e8f0', borderRadius: '12px' },
    searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '15px', color: '#0f172a' },
    filterBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', backgroundColor: '#fff', border: '2px solid #e2e8f0', borderRadius: '12px' },
    filterSelect: { border: 'none', outline: 'none', fontSize: '14px', color: '#0f172a', fontWeight: '600', cursor: 'pointer', backgroundColor: 'transparent' },

    // 그리드
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' },

    // 카드 (컴팩트, 단가 중심)
    card: { backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    cardHeader: { display: 'flex', gap: '12px', alignItems: 'center', padding: '14px 14px 0' },
    cardAvatar: { width: '48px', height: '48px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e2e8f0' },
    cardAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    cardTitle: { margin: '0', fontSize: '15px', fontWeight: '700', color: '#0f172a', lineHeight: '1.3' },
    cardSlogan: { margin: '2px 0 0', fontSize: '11px', color: '#6366f1', fontWeight: '500' },
    cardBody: { padding: '10px 14px 14px' },
    cardPriceRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
    cardPrice: { fontSize: '12px', color: '#334155', fontWeight: '500' },
    cardDays: { fontSize: '11px', color: '#94a3b8', fontWeight: '400' },
    cardSpecs: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' },
    specBadge: { padding: '2px 7px', backgroundColor: '#f1f5f9', color: '#64748b', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
    cardBenefit: { padding: '6px 10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#92400e', marginBottom: '8px' },
    cardTags: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' },
    cardTag: { padding: '2px 7px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: '4px', fontSize: '10px', fontWeight: '600' },
    cardTagMore: { padding: '2px 7px', backgroundColor: '#f1f5f9', color: '#94a3b8', borderRadius: '4px', fontSize: '10px', fontWeight: '600' },
    cardInfoRow: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#94a3b8', marginBottom: '10px' },
    cardActions: { display: 'flex', gap: '6px' },
    btnPrimary: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    btnChat: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px 12px', backgroundColor: '#eff6ff', color: '#3b82f6', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    heartBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s', flexShrink: 0 },

    // 빈 상태
    empty: { textAlign: 'center', padding: '80px 20px', backgroundColor: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0' },
    emptyTitle: { marginTop: '16px', fontSize: '18px', fontWeight: '700', color: '#0f172a' },
    emptySub: { fontSize: '14px', color: '#94a3b8' },
};

export default LabDirectory;
